import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getStats,
  getAdminBlogs,
  deleteBlog,
  getUsers,
  createMember,
  deleteMember,
  setMemberStatus,
  setMemberPassword,
  getEvents,
  createEvent,
  updateEvent,
  deleteEvent,
  exportEventRegistrations,
  uploadImage,
  resolveAssetUrl,
} from '../api/api';
import { useAuth } from '../context/AuthContext';

const categoryColors = {
  technical: '#0f3460',
  news: '#e8920c',
  event: '#198754',
  announcement: '#dc3545',
  general: '#6c757d',
};

function StatusBadge({ status, t }) {
  return (
    <span
      style={{
        padding: '3px 10px',
        borderRadius: '999px',
        fontSize: '11px',
        fontWeight: 600,
        textTransform: 'uppercase',
        background: status === 'published' ? '#d1fae5' : '#fef3c7',
        color: status === 'published' ? '#065f46' : '#92400e',
      }}
    >
      {status === 'published' ? t('Home.Admin.Published') : t('Home.Admin.Draft')}
    </span>
  );
}

function StatCard({ icon, label, value, color }) {
  return (
    <div
      style={{
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        borderLeft: `4px solid ${color}`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <div
          style={{
            width: '48px',
            height: '48px',
            borderRadius: '12px',
            background: `${color}22`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '22px',
            color,
          }}
        >
          <i className={`bi bi-${icon}`} />
        </div>
        <div>
          <div style={{ fontSize: '28px', fontWeight: 700, lineHeight: 1 }}>{value}</div>
          <div style={{ color: '#6c757d', fontSize: '13px', marginTop: '4px' }}>{label}</div>
        </div>
      </div>
    </div>
  );
}

const NAV_ITEMS = [
  { icon: 'speedometer2', key: 'Dashboard', section: 'dashboard' },
  { icon: 'journal-text', key: 'BlogPosts', section: 'posts' },
  { icon: 'calendar-event', key: 'Events', section: 'events' },
  { icon: 'people', key: 'Members', section: 'members' },
];

export default function Admin() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  // Admin dashboard is always Arabic / RTL regardless of the site language
  const t = i18n.getFixedT('ar');
  const isRTL = true;

  const [activeSection, setActiveSection] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [blogs, setBlogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsLoaded, setEventsLoaded] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    Promise.all([getStats(), getAdminBlogs()])
      .then(([s, b]) => {
        setStats(s.data);
        setBlogs(b.data);
      })
      .finally(() => setLoading(false));
  }, []);

  const loadEvents = () => {
    setEventsLoading(true);
    getEvents()
      .then((res) => setEvents(res.data))
      .catch(() => {})
      .finally(() => { setEventsLoading(false); setEventsLoaded(true); });
  };

  const handleSectionChange = (section) => {
    setActiveSection(section);
    if (section === 'members' && !usersLoaded) {
      setUsersLoading(true);
      getUsers()
        .then((res) => setUsers(res.data))
        .catch(() => {})
        .finally(() => { setUsersLoading(false); setUsersLoaded(true); });
    }
    if (section === 'events' && !eventsLoaded) {
      loadEvents();
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('Home.Admin.DeleteConfirm'))) return;
    setDeleteId(id);
    try {
      await deleteBlog(id);
      const deleted = blogs.find((b) => b.id === id);
      setBlogs((prev) => prev.filter((b) => b.id !== id));
      if (stats && deleted) {
        setStats((prev) => ({
          ...prev,
          totalBlogs: prev.totalBlogs - 1,
          publishedBlogs: deleted.status === 'published' ? prev.publishedBlogs - 1 : prev.publishedBlogs,
          draftBlogs: deleted.status === 'draft' ? prev.draftBlogs - 1 : prev.draftBlogs,
        }));
      }
    } finally {
      setDeleteId(null);
    }
  };

  const categoryLabel = (key) =>
    ({
      technical: t('Home.Blog.Technical'),
      news: t('Home.Blog.News'),
      event: t('Home.Blog.Event'),
      announcement: t('Home.Blog.Announcement'),
      general: t('Home.Blog.General'),
    }[key] || key);

  return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9', direction: isRTL ? 'rtl' : 'ltr' }}>
      <div style={{ display: 'flex', minHeight: '100vh' }}>
        {/* ── Sidebar ── */}
        <div
          style={{
            width: '240px',
            background: 'linear-gradient(180deg, #1a1a2e 0%, #0f3460 100%)',
            color: 'white',
            display: 'flex',
            flexDirection: 'column',
            flexShrink: 0,
          }}
        >
          <div style={{ padding: '28px 24px 20px' }}>
            <img src="/img/logo.png" alt="ENGPI" height="45" />
            <div style={{ fontSize: '11px', opacity: 0.5, marginTop: '8px', letterSpacing: '1px' }}>
              {t('Home.Admin.PanelTitle')}
            </div>
          </div>

          <nav style={{ flex: 1, padding: '8px 12px' }}>
            {NAV_ITEMS.map((item) => {
              const isActive = activeSection === item.section;
              return (
                <div
                  key={item.section}
                  onClick={() => handleSectionChange(item.section)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px 12px',
                    borderRadius: '8px',
                    background: isActive ? 'rgba(255,255,255,0.12)' : 'transparent',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: isActive ? 600 : 400,
                    marginBottom: '4px',
                    transition: 'background 0.15s',
                    userSelect: 'none',
                  }}
                  onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
                  onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                >
                  <i className={`bi bi-${item.icon}`} />
                  {t(`Home.Admin.${item.key}`)}
                </div>
              );
            })}
          </nav>

          <div style={{ padding: '16px 24px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
            <div style={{ fontSize: '13px', opacity: 0.7 }}>{user?.fullName}</div>
            <div style={{ fontSize: '11px', opacity: 0.45, marginTop: '2px' }}>{user?.email}</div>
            <button
              onClick={() => { logout(); navigate('/login'); }}
              style={{
                marginTop: '12px',
                background: 'rgba(255,255,255,0.1)',
                border: '1px solid rgba(255,255,255,0.2)',
                color: 'white',
                borderRadius: '6px',
                padding: '6px 12px',
                fontSize: '12px',
                cursor: 'pointer',
                width: '100%',
              }}
            >
              <i className="bi bi-box-arrow-left me-1" /> {t('Home.Admin.Logout')}
            </button>
          </div>
        </div>

        {/* ── Main Content ── */}
        <div style={{ flex: 1, padding: '32px', overflowY: 'auto' }}>
          {activeSection === 'dashboard' && (
            <DashboardSection
              stats={stats}
              blogs={blogs}
              loading={loading}
              t={t}
              i18n={i18n}
              isRTL={isRTL}
              user={user}
              categoryLabel={categoryLabel}
              onGoToPosts={() => setActiveSection('posts')}
            />
          )}

          {activeSection === 'posts' && (
            <PostsSection
              blogs={blogs}
              loading={loading}
              t={t}
              i18n={i18n}
              isRTL={isRTL}
              categoryLabel={categoryLabel}
              deleteId={deleteId}
              onDelete={handleDelete}
            />
          )}

          {activeSection === 'events' && (
            <EventsSection
              events={events}
              setEvents={setEvents}
              loading={eventsLoading}
              t={t}
              isRTL={isRTL}
            />
          )}

          {activeSection === 'members' && (
            <MembersSection
              users={users}
              setUsers={setUsers}
              loading={usersLoading}
              t={t}
              i18n={i18n}
              isRTL={isRTL}
              currentUserId={user?.id}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Dashboard Section ── */
function DashboardSection({ stats, blogs, loading, t, i18n, isRTL, user, categoryLabel, onGoToPosts }) {
  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '32px',
          flexDirection: isRTL ? 'row-reverse' : 'row',
        }}
      >
        <div style={{ textAlign: isRTL ? 'right' : 'left' }}>
          <h4 style={{ fontWeight: 700, margin: 0 }}>{t('Home.Admin.Dashboard')}</h4>
          <p style={{ color: '#6c757d', margin: 0, fontSize: '14px' }}>
            {t('Home.Admin.WelcomeBack')}, {user?.fullName}
          </p>
        </div>
        <Link
          to="/admin/posts/new"
          style={{
            background: 'linear-gradient(135deg, #f5a623, #e8920c)',
            color: 'white',
            textDecoration: 'none',
            padding: '10px 20px',
            borderRadius: '8px',
            fontWeight: 600,
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <i className="bi bi-plus-lg" /> {t('Home.Admin.NewPost')}
        </Link>
      </div>

      {stats && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '16px',
            marginBottom: '32px',
          }}
        >
          <StatCard icon="journal-text" label={t('Home.Admin.TotalPosts')} value={stats.totalBlogs} color="#0f3460" />
          <StatCard icon="check-circle" label={t('Home.Admin.Published')} value={stats.publishedBlogs} color="#198754" />
          <StatCard icon="pencil-square" label={t('Home.Admin.Drafts')} value={stats.draftBlogs} color="#e8920c" />
          <StatCard icon="people" label={t('Home.Admin.Members')} value={stats.totalUsers} color="#6f42c1" />
        </div>
      )}

      {/* Recent Posts Preview */}
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid #f1f5f9',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: isRTL ? 'row-reverse' : 'row',
          }}
        >
          <h6 style={{ fontWeight: 700, margin: 0 }}>{t('Home.Admin.RecentPosts')}</h6>
          <button
            onClick={onGoToPosts}
            style={{
              background: 'none',
              border: 'none',
              color: '#0f3460',
              fontSize: '13px',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            {t('Home.Admin.ViewAll')} →
          </button>
        </div>

        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-warning" role="status" />
          </div>
        ) : blogs.length === 0 ? (
          <div className="text-center py-4 text-muted">
            <p className="mb-0">{t('Home.Admin.NoPosts')}</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '760px' }}>
            <tbody>
              {blogs.slice(0, 5).map((blog, i) => (
                <tr key={blog.id} style={{ borderTop: i === 0 ? 'none' : '1px solid #f1f5f9', fontSize: '14px' }}>
                  <td style={{ padding: '12px 24px', fontWeight: 600, maxWidth: '300px' }}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {blog.title}
                    </div>
                  </td>
                  <td style={{ padding: '12px 24px' }}>
                    <span
                      style={{
                        background: `${categoryColors[blog.category]}22`,
                        color: categoryColors[blog.category],
                        padding: '3px 10px',
                        borderRadius: '999px',
                        fontSize: '12px',
                        fontWeight: 600,
                      }}
                    >
                      {categoryLabel(blog.category)}
                    </span>
                  </td>
                  <td style={{ padding: '12px 24px' }}>
                    <StatusBadge status={blog.status} t={t} />
                  </td>
                  <td style={{ padding: '12px 24px', color: '#6c757d', fontSize: '13px' }}>
                    {new Date(blog.createdAt).toLocaleDateString('ar')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </>
  );
}

/* ── Posts Section ── */
function PostsSection({ blogs, loading, t, i18n, isRTL, categoryLabel, deleteId, onDelete }) {
  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '32px',
          flexDirection: isRTL ? 'row-reverse' : 'row',
        }}
      >
        <h4 style={{ fontWeight: 700, margin: 0 }}>{t('Home.Admin.BlogPosts')}</h4>
        <Link
          to="/admin/posts/new"
          style={{
            background: 'linear-gradient(135deg, #f5a623, #e8920c)',
            color: 'white',
            textDecoration: 'none',
            padding: '10px 20px',
            borderRadius: '8px',
            fontWeight: 600,
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <i className="bi bi-plus-lg" /> {t('Home.Admin.NewPost')}
        </Link>
      </div>

      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid #f1f5f9',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: isRTL ? 'row-reverse' : 'row',
          }}
        >
          <h6 style={{ fontWeight: 700, margin: 0 }}>{t('Home.Admin.AllPosts')}</h6>
          <span style={{ fontSize: '13px', color: '#6c757d' }}>
            {blogs.length} {t('Home.Admin.Posts')}
          </span>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-warning" role="status" />
          </div>
        ) : blogs.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <i className="bi bi-journal-x" style={{ fontSize: '2.5rem' }} />
            <p className="mt-2">{t('Home.Admin.NoPosts')}</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '760px' }}>
            <thead>
              <tr
                style={{
                  background: '#f8fafc',
                  fontSize: '12px',
                  color: '#6c757d',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                {['Title', 'Category', 'Status', 'Date', 'Actions'].map((h) => (
                  <th
                    key={h}
                    style={{ padding: '12px 24px', textAlign: isRTL ? 'right' : 'left', fontWeight: 600 }}
                  >
                    {t(`Home.Admin.${h}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {blogs.map((blog, i) => (
                <tr
                  key={blog.id}
                  style={{ borderTop: i === 0 ? 'none' : '1px solid #f1f5f9', fontSize: '14px' }}
                >
                  <td style={{ padding: '14px 24px', fontWeight: 600, maxWidth: '280px' }}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {blog.title}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>/{blog.slug}</div>
                  </td>
                  <td style={{ padding: '14px 24px' }}>
                    <span
                      style={{
                        background: `${categoryColors[blog.category]}22`,
                        color: categoryColors[blog.category],
                        padding: '3px 10px',
                        borderRadius: '999px',
                        fontSize: '12px',
                        fontWeight: 600,
                      }}
                    >
                      {categoryLabel(blog.category)}
                    </span>
                  </td>
                  <td style={{ padding: '14px 24px' }}>
                    <StatusBadge status={blog.status} t={t} />
                  </td>
                  <td style={{ padding: '14px 24px', color: '#6c757d', fontSize: '13px' }}>
                    {new Date(blog.createdAt).toLocaleDateString('ar')}
                  </td>
                  <td style={{ padding: '14px 24px' }}>
                    <div style={{ display: 'flex', gap: '8px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                      <Link
                        to={`/admin/posts/${blog.id}/edit`}
                        style={{
                          padding: '5px 12px',
                          borderRadius: '6px',
                          background: '#e0f2fe',
                          color: '#0369a1',
                          textDecoration: 'none',
                          fontSize: '12px',
                          fontWeight: 600,
                        }}
                      >
                        {t('Home.Admin.Edit')}
                      </Link>
                      <button
                        onClick={() => onDelete(blog.id)}
                        disabled={deleteId === blog.id}
                        style={{
                          padding: '5px 12px',
                          borderRadius: '6px',
                          background: '#fee2e2',
                          color: '#dc2626',
                          border: 'none',
                          fontSize: '12px',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        {deleteId === blog.id ? '…' : t('Home.Admin.Delete')}
                      </button>
                      <a
                        href={`/blog/${blog.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          padding: '5px 12px',
                          borderRadius: '6px',
                          background: '#f0fdf4',
                          color: '#16a34a',
                          textDecoration: 'none',
                          fontSize: '12px',
                          fontWeight: 600,
                        }}
                      >
                        {t('Home.Admin.View')}
                      </a>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
    </>
  );
}

/* ── Events Section ── */
const eventStatusColors = {
  upcoming: { bg: '#d1fae5', color: '#065f46' },
  completed: { bg: '#e0e7ff', color: '#3730a3' },
  cancelled: { bg: '#fee2e2', color: '#dc2626' },
};

// Convert an ISO/date string to the value a datetime-local input expects (local time)
function toLocalInput(value) {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d)) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const emptyEvent = {
  title: '',
  description: '',
  date: '',
  location: '',
  capacity: '',
  coverImage: '',
  status: 'upcoming',
  registrationOpen: true,
};

function EventsSection({ events, setEvents, loading, t, isRTL }) {
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null); // event being edited, or null for new
  const [form, setForm] = useState(emptyEvent);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [downloadId, setDownloadId] = useState(null);
  const [toggleId, setToggleId] = useState(null);
  const [coverUploading, setCoverUploading] = useState(false);
  const coverInputRef = useRef(null);

  const handleCoverFile = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setCoverUploading(true);
    setFormError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await uploadImage(fd);
      setForm((f) => ({ ...f, coverImage: res.data.url }));
    } catch {
      setFormError(t('Home.Admin.UploadFailed', 'Image upload failed. Please try again.'));
    } finally {
      setCoverUploading(false);
    }
  };

  const statusLabel = (s) =>
    ({
      upcoming: t('Home.Admin.Upcoming'),
      completed: t('Home.Admin.Completed'),
      cancelled: t('Home.Admin.Cancelled'),
    }[s] || s);

  const openNew = () => {
    setEditing(null);
    setForm(emptyEvent);
    setFormError('');
    setShowModal(true);
  };

  const openEdit = (ev) => {
    setEditing(ev);
    setForm({
      title: ev.title || '',
      description: ev.description || '',
      date: toLocalInput(ev.date),
      location: ev.location || '',
      capacity: ev.capacity != null ? String(ev.capacity) : '',
      coverImage: ev.coverImage || '',
      status: ev.status || 'upcoming',
      registrationOpen: ev.registrationOpen !== false,
    });
    setFormError('');
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.date) return;
    setSaving(true);
    setFormError('');
    const payload = {
      title: form.title.trim(),
      description: form.description.trim() || null,
      date: new Date(form.date).toISOString(),
      location: form.location.trim() || null,
      capacity: form.capacity === '' ? null : Number(form.capacity),
      coverImage: form.coverImage.trim() || null,
      status: form.status,
      registrationOpen: form.registrationOpen,
    };
    try {
      if (editing) {
        const res = await updateEvent(editing.id, payload);
        setEvents((prev) =>
          prev.map((x) => (x.id === editing.id ? { ...x, ...res.data, registrationCount: x.registrationCount } : x)),
        );
      } else {
        const res = await createEvent(payload);
        setEvents((prev) => [...prev, { ...res.data, registrationCount: 0 }]);
      }
      setShowModal(false);
    } catch (err) {
      const msg = err.response?.data?.message;
      setFormError(Array.isArray(msg) ? msg[0] : (msg || 'Error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('Home.Admin.DeleteEventConfirm'))) return;
    setDeleteId(id);
    try {
      await deleteEvent(id);
      setEvents((prev) => prev.filter((x) => x.id !== id));
    } finally {
      setDeleteId(null);
    }
  };

  const handleToggleRegistration = async (ev) => {
    const next = ev.registrationOpen === false; // currently closed → open it
    setToggleId(ev.id);
    try {
      const res = await updateEvent(ev.id, { registrationOpen: next });
      setEvents((prev) =>
        prev.map((x) =>
          x.id === ev.id ? { ...x, ...res.data, registrationCount: x.registrationCount } : x,
        ),
      );
    } catch (err) {
      window.alert(err.response?.data?.message || 'Error');
    } finally {
      setToggleId(null);
    }
  };

  const handleDownload = async (ev) => {
    setDownloadId(ev.id);
    try {
      const res = await exportEventRegistrations(ev.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      // Try to read filename from header, else fall back
      const cd = res.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^"]+)"?/);
      link.download = match ? match[1] : `registrations-${ev.id}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      window.alert(err.response?.data?.message || 'Error');
    } finally {
      setDownloadId(null);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 14px',
    border: '1.5px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  };
  const labelStyle = {
    display: 'block',
    fontSize: '13px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '6px',
  };

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '32px',
          flexDirection: isRTL ? 'row-reverse' : 'row',
        }}
      >
        <div style={{ textAlign: isRTL ? 'right' : 'left' }}>
          <h4 style={{ fontWeight: 700, margin: 0 }}>{t('Home.Admin.Events')}</h4>
        </div>
        <button
          onClick={openNew}
          style={{
            background: 'linear-gradient(135deg, #f5a623, #e8920c)',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '8px',
            fontWeight: 600,
            fontSize: '14px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <i className="bi bi-plus-lg" /> {t('Home.Admin.NewEvent')}
        </button>
      </div>

      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid #f1f5f9',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: isRTL ? 'row-reverse' : 'row',
          }}
        >
          <h6 style={{ fontWeight: 700, margin: 0 }}>{t('Home.Admin.AllEvents')}</h6>
          <span style={{ fontSize: '13px', color: '#6c757d' }}>
            {events.length} {t('Home.Admin.EventsCount')}
          </span>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-warning" role="status" />
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <i className="bi bi-calendar-x" style={{ fontSize: '2.5rem' }} />
            <p className="mt-2">{t('Home.Admin.NoEvents')}</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '820px' }}>
              <thead>
                <tr
                  style={{
                    background: '#f8fafc',
                    fontSize: '12px',
                    color: '#6c757d',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  {['EventTitle', 'EventDate', 'EventLocation', 'Registrations', 'EventStatus', 'Actions'].map((h) => (
                    <th key={h} style={{ padding: '12px 24px', textAlign: isRTL ? 'right' : 'left', fontWeight: 600 }}>
                      {t(`Home.Admin.${h}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {events.map((ev, i) => {
                  const sc = eventStatusColors[ev.status] || eventStatusColors.upcoming;
                  return (
                    <tr key={ev.id} style={{ borderTop: i === 0 ? 'none' : '1px solid #f1f5f9', fontSize: '14px' }}>
                      <td style={{ padding: '14px 24px', fontWeight: 600, maxWidth: '240px' }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ev.title}</div>
                      </td>
                      <td style={{ padding: '14px 24px', color: '#374151', fontSize: '13px', whiteSpace: 'nowrap' }}>
                        {new Date(ev.date).toLocaleString('ar', {
                          year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                        })}
                      </td>
                      <td style={{ padding: '14px 24px', color: '#6c757d', fontSize: '13px' }}>{ev.location || '—'}</td>
                      <td style={{ padding: '14px 24px' }}>
                        <span style={{ fontWeight: 700 }}>{ev.registrationCount}</span>
                        {ev.capacity != null && <span style={{ color: '#9ca3af' }}> / {ev.capacity}</span>}
                      </td>
                      <td style={{ padding: '14px 24px' }}>
                        <span
                          style={{
                            background: sc.bg, color: sc.color, padding: '3px 10px',
                            borderRadius: '999px', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase',
                          }}
                        >
                          {statusLabel(ev.status)}
                        </span>
                        {ev.registrationOpen === false && (
                          <div
                            style={{
                              marginTop: '6px', display: 'inline-flex', alignItems: 'center', gap: '4px',
                              background: '#fef3c7', color: '#92400e', padding: '2px 8px',
                              borderRadius: '999px', fontSize: '10px', fontWeight: 600,
                            }}
                          >
                            <i className="bi bi-lock-fill" />
                            {t('Home.Admin.RegistrationClosed')}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '14px 24px' }}>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                          <button
                            onClick={() => handleDownload(ev)}
                            disabled={downloadId === ev.id || ev.registrationCount === 0}
                            title={ev.registrationCount === 0 ? t('Home.Admin.NoRegistrations') : ''}
                            style={{
                              padding: '5px 12px', borderRadius: '6px',
                              background: ev.registrationCount === 0 ? '#f1f5f9' : '#dcfce7',
                              color: ev.registrationCount === 0 ? '#9ca3af' : '#15803d',
                              border: 'none', fontSize: '12px', fontWeight: 600,
                              cursor: ev.registrationCount === 0 ? 'not-allowed' : 'pointer',
                            }}
                          >
                            <i className="bi bi-file-earmark-excel me-1" />
                            {downloadId === ev.id ? '…' : t('Home.Admin.DownloadExcel')}
                          </button>
                          <button
                            onClick={() => handleToggleRegistration(ev)}
                            disabled={toggleId === ev.id}
                            title={ev.registrationOpen === false ? t('Home.Admin.OpenRegistration') : t('Home.Admin.CloseRegistration')}
                            style={{
                              padding: '5px 12px', borderRadius: '6px',
                              background: ev.registrationOpen === false ? '#dcfce7' : '#fef3c7',
                              color: ev.registrationOpen === false ? '#15803d' : '#92400e',
                              border: 'none', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                            }}
                          >
                            <i className={`bi bi-${ev.registrationOpen === false ? 'unlock' : 'lock'} me-1`} />
                            {toggleId === ev.id
                              ? '…'
                              : ev.registrationOpen === false
                                ? t('Home.Admin.OpenRegistration')
                                : t('Home.Admin.CloseRegistration')}
                          </button>
                          <button
                            onClick={() => openEdit(ev)}
                            style={{
                              padding: '5px 12px', borderRadius: '6px', background: '#e0f2fe',
                              color: '#0369a1', border: 'none', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                            }}
                          >
                            {t('Home.Admin.Edit')}
                          </button>
                          <button
                            onClick={() => handleDelete(ev.id)}
                            disabled={deleteId === ev.id}
                            style={{
                              padding: '5px 12px', borderRadius: '6px', background: '#fee2e2',
                              color: '#dc2626', border: 'none', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                            }}
                          >
                            {deleteId === ev.id ? '…' : t('Home.Admin.Delete')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Event Modal ── */}
      {showModal && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px',
          }}
        >
          <div
            style={{
              background: 'white', borderRadius: '16px', width: '100%', maxWidth: '520px',
              maxHeight: '90vh', overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.25)', direction: isRTL ? 'rtl' : 'ltr',
            }}
          >
            <div
              style={{
                padding: '20px 24px', borderBottom: '1px solid #f1f5f9',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                position: 'sticky', top: 0, background: 'white',
              }}
            >
              <h5 style={{ margin: 0, fontWeight: 700, fontSize: '17px' }}>
                <i className="bi bi-calendar-event me-2" style={{ color: '#f5a623' }} />
                {editing ? t('Home.Admin.EditEvent') : t('Home.Admin.AddEventTitle')}
              </h5>
              <button
                onClick={() => setShowModal(false)}
                style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6c757d', lineHeight: 1 }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleSave} style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={labelStyle}>{t('Home.Admin.EventTitle')}</label>
                <input
                  style={inputStyle}
                  placeholder={t('Home.Admin.EventTitlePlaceholder')}
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  required
                  autoFocus
                />
              </div>

              <div>
                <label style={labelStyle}>{t('Home.Admin.EventDescription')}</label>
                <textarea
                  style={{ ...inputStyle, minHeight: '90px', resize: 'vertical' }}
                  placeholder={t('Home.Admin.EventDescriptionPlaceholder')}
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>{t('Home.Admin.EventDate')}</label>
                  <input
                    type="datetime-local"
                    style={inputStyle}
                    value={form.date}
                    onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                    required
                  />
                </div>
                <div style={{ width: '130px' }}>
                  <label style={labelStyle}>{t('Home.Admin.EventCapacity')}</label>
                  <input
                    type="number"
                    min="1"
                    style={inputStyle}
                    placeholder="∞"
                    value={form.capacity}
                    onChange={(e) => setForm((f) => ({ ...f, capacity: e.target.value }))}
                  />
                </div>
              </div>

              <div>
                <label style={labelStyle}>{t('Home.Admin.EventLocation')}</label>
                <input
                  style={inputStyle}
                  placeholder={t('Home.Admin.EventLocationPlaceholder')}
                  value={form.location}
                  onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                />
              </div>

              <div>
                <label style={labelStyle}>{t('Home.Admin.EventCoverImage')}</label>

                <input
                  ref={coverInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleCoverFile}
                />

                {form.coverImage && (
                  <div style={{ position: 'relative', marginBottom: '10px' }}>
                    <img
                      src={resolveAssetUrl(form.coverImage)}
                      alt="cover"
                      style={{ width: '100%', maxHeight: '160px', objectFit: 'cover', borderRadius: '8px', display: 'block' }}
                    />
                    <button
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, coverImage: '' }))}
                      title={t('Home.Admin.Delete')}
                      style={{
                        position: 'absolute', top: '8px', [isRTL ? 'left' : 'right']: '8px',
                        width: '26px', height: '26px', borderRadius: '50%', border: 'none',
                        background: 'rgba(0,0,0,0.55)', color: 'white', cursor: 'pointer', fontSize: '14px', lineHeight: 1,
                      }}
                    >
                      ×
                    </button>
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => coverInputRef.current?.click()}
                  disabled={coverUploading}
                  style={{
                    width: '100%', padding: '10px', borderRadius: '8px',
                    border: '1.5px dashed #d1d5db', background: '#f9fafb', color: '#374151',
                    fontSize: '13px', fontWeight: 600, cursor: coverUploading ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  }}
                >
                  {coverUploading ? (
                    <>
                      <span className="spinner-border spinner-border-sm text-warning" role="status" />
                      {t('Home.Admin.Uploading', 'Uploading…')}
                    </>
                  ) : (
                    <>
                      <i className="bi bi-upload" />
                      {form.coverImage ? t('Home.Admin.ReplaceImage', 'Replace Image') : t('Home.Admin.UploadImage', 'Upload Image')}
                    </>
                  )}
                </button>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '10px 0' }}>
                  <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }} />
                  <span style={{ fontSize: '11px', color: '#9ca3af', fontWeight: 600 }}>{t('Home.Admin.Or', 'OR')}</span>
                  <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }} />
                </div>

                <input
                  style={inputStyle}
                  placeholder={t('Home.Admin.EventCoverImagePlaceholder')}
                  value={form.coverImage}
                  onChange={(e) => setForm((f) => ({ ...f, coverImage: e.target.value }))}
                />
              </div>

              <div>
                <label style={labelStyle}>{t('Home.Admin.EventStatus')}</label>
                <select
                  style={{ ...inputStyle, background: 'white' }}
                  value={form.status}
                  onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                >
                  <option value="upcoming">{t('Home.Admin.Upcoming')}</option>
                  <option value="completed">{t('Home.Admin.Completed')}</option>
                  <option value="cancelled">{t('Home.Admin.Cancelled')}</option>
                </select>
              </div>

              <label
                style={{
                  display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer',
                  background: '#f9fafb', border: '1.5px solid #e5e7eb', borderRadius: '8px', padding: '12px 14px',
                  flexDirection: isRTL ? 'row-reverse' : 'row',
                }}
              >
                <input
                  type="checkbox"
                  checked={form.registrationOpen}
                  onChange={(e) => setForm((f) => ({ ...f, registrationOpen: e.target.checked }))}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>
                  {t('Home.Admin.RegistrationOpenLabel')}
                </span>
              </label>

              {formError && (
                <div style={{ background: '#fee2e2', color: '#dc2626', padding: '10px 14px', borderRadius: '8px', fontSize: '13px' }}>
                  {formError}
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '4px' }}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{
                    padding: '10px 20px', borderRadius: '8px', border: '1.5px solid #e5e7eb',
                    background: 'white', fontSize: '14px', fontWeight: 600, cursor: 'pointer', color: '#374151',
                  }}
                >
                  {t('Home.Admin.Cancel')}
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  style={{
                    padding: '10px 24px', borderRadius: '8px',
                    background: 'linear-gradient(135deg, #f5a623, #e8920c)', color: 'white',
                    border: 'none', fontSize: '14px', fontWeight: 600,
                    cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1,
                  }}
                >
                  {saving ? t('Home.Admin.SavingEvent') : t('Home.Admin.SaveEvent')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

/* ── Members Section ── */
function MembersSection({ users, setUsers, loading, t, i18n, isRTL, currentUserId }) {
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ fullName: '', email: '', password: '', role: 'member' });
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [statusId, setStatusId] = useState(null);

  // Change-password modal state
  const [pwTarget, setPwTarget] = useState(null); // the member whose password we're changing
  const [pwValue, setPwValue] = useState('');
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState('');
  const [pwDone, setPwDone] = useState(false);

  const openModal = () => {
    setForm({ fullName: '', email: '', password: '', role: 'member' });
    setFormError('');
    setShowModal(true);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.fullName.trim() || !form.email.trim() || !form.password.trim()) return;
    setSaving(true);
    setFormError('');
    try {
      const res = await createMember(form);
      setUsers((prev) => [...prev, res.data]);
      setShowModal(false);
    } catch (err) {
      const msg = err.response?.data?.message;
      if (err.response?.status === 409 || (typeof msg === 'string' && msg.toLowerCase().includes('email'))) {
        setFormError(t('Home.Admin.EmailInUse'));
      } else {
        setFormError(Array.isArray(msg) ? msg[0] : (msg || 'Error'));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('Home.Admin.DeleteMemberConfirm'))) return;
    setDeleteId(id);
    try {
      await deleteMember(id);
      setUsers((prev) => prev.filter((u) => u.id !== id));
    } finally {
      setDeleteId(null);
    }
  };

  const handleToggleStatus = async (member) => {
    const nextActive = !member.isActive;
    const msg = nextActive ? t('Home.Admin.ActivateConfirm') : t('Home.Admin.DeactivateConfirm');
    if (!window.confirm(msg)) return;
    setStatusId(member.id);
    try {
      const res = await setMemberStatus(member.id, nextActive);
      setUsers((prev) => prev.map((u) => (u.id === member.id ? { ...u, isActive: res.data.isActive } : u)));
    } catch (err) {
      window.alert(err.response?.data?.message || 'Error');
    } finally {
      setStatusId(null);
    }
  };

  const openPwModal = (member) => {
    setPwTarget(member);
    setPwValue('');
    setPwError('');
    setPwDone(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwValue.length < 6) {
      setPwError(t('Home.Admin.PasswordTooShort'));
      return;
    }
    setPwSaving(true);
    setPwError('');
    try {
      await setMemberPassword(pwTarget.id, pwValue);
      setPwDone(true);
      setPwValue('');
    } catch (err) {
      setPwError(err.response?.data?.message || 'Error');
    } finally {
      setPwSaving(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 14px',
    border: '1.5px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
  };

  const labelStyle = {
    display: 'block',
    fontSize: '13px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '6px',
  };

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '32px',
          flexDirection: isRTL ? 'row-reverse' : 'row',
        }}
      >
        <div style={{ textAlign: isRTL ? 'right' : 'left' }}>
          <h4 style={{ fontWeight: 700, margin: 0 }}>{t('Home.Admin.Members')}</h4>
          <p style={{ color: '#6c757d', margin: 0, fontSize: '14px' }}>{t('Home.Admin.MembersSubtitle')}</p>
        </div>
        <button
          onClick={openModal}
          style={{
            background: 'linear-gradient(135deg, #f5a623, #e8920c)',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '8px',
            fontWeight: 600,
            fontSize: '14px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <i className="bi bi-person-plus" /> {t('Home.Admin.AddMember')}
        </button>
      </div>

      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid #f1f5f9',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: isRTL ? 'row-reverse' : 'row',
          }}
        >
          <h6 style={{ fontWeight: 700, margin: 0 }}>{t('Home.Admin.AllMembers')}</h6>
          <span style={{ fontSize: '13px', color: '#6c757d' }}>
            {users.length} {t('Home.Admin.MembersCount')}
          </span>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-warning" role="status" />
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <i className="bi bi-people" style={{ fontSize: '2.5rem' }} />
            <p className="mt-2">{t('Home.Admin.NoMembers')}</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '760px' }}>
            <thead>
              <tr
                style={{
                  background: '#f8fafc',
                  fontSize: '12px',
                  color: '#6c757d',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                {['MemberName', 'Email', 'Role', 'MemberStatus', 'JoinedDate', 'Actions'].map((h) => (
                  <th
                    key={h}
                    style={{ padding: '12px 24px', textAlign: isRTL ? 'right' : 'left', fontWeight: 600 }}
                  >
                    {t(`Home.Admin.${h}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((member, i) => (
                <tr
                  key={member.id}
                  style={{ borderTop: i === 0 ? 'none' : '1px solid #f1f5f9', fontSize: '14px' }}
                >
                  <td style={{ padding: '14px 24px', fontWeight: 600 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                      <div
                        style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #1a1a2e, #0f3460)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '14px',
                          fontWeight: 700,
                          flexShrink: 0,
                        }}
                      >
                        {(member.fullName || '?')[0].toUpperCase()}
                      </div>
                      <span>{member.fullName}</span>
                      {member.id === currentUserId && (
                        <span
                          style={{
                            background: '#fff7e6',
                            color: '#d97706',
                            border: '1px solid #f5a623',
                            padding: '1px 8px',
                            borderRadius: '999px',
                            fontSize: '10px',
                            fontWeight: 700,
                          }}
                        >
                          {t('Home.Admin.You')}
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '14px 24px', color: '#374151' }}>{member.email}</td>
                  <td style={{ padding: '14px 24px' }}>
                    <span
                      style={{
                        padding: '3px 10px',
                        borderRadius: '999px',
                        fontSize: '11px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        background: member.role === 'admin' ? '#ede9fe' : '#e0f2fe',
                        color: member.role === 'admin' ? '#6d28d9' : '#0369a1',
                      }}
                    >
                      {member.role === 'admin' ? t('Home.Admin.AdminRole') : t('Home.Admin.MemberRole')}
                    </span>
                  </td>
                  <td style={{ padding: '14px 24px' }}>
                    <span
                      style={{
                        padding: '3px 10px',
                        borderRadius: '999px',
                        fontSize: '11px',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        background: member.isActive ? '#d1fae5' : '#fee2e2',
                        color: member.isActive ? '#065f46' : '#dc2626',
                      }}
                    >
                      {member.isActive ? t('Home.Admin.Active') : t('Home.Admin.Inactive')}
                    </span>
                  </td>
                  <td style={{ padding: '14px 24px', color: '#6c757d', fontSize: '13px' }}>
                    {new Date(member.createdAt).toLocaleDateString('ar')}
                  </td>
                  <td style={{ padding: '14px 24px' }}>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                      {/* Change password */}
                      <button
                        onClick={() => openPwModal(member)}
                        style={{
                          padding: '5px 12px',
                          borderRadius: '6px',
                          background: '#e0f2fe',
                          color: '#0369a1',
                          border: 'none',
                          fontSize: '12px',
                          fontWeight: 600,
                          cursor: 'pointer',
                        }}
                      >
                        {t('Home.Admin.ChangePassword')}
                      </button>

                      {/* Activate / Deactivate — not for self */}
                      {member.id !== currentUserId && (
                        <button
                          onClick={() => handleToggleStatus(member)}
                          disabled={statusId === member.id}
                          style={{
                            padding: '5px 12px',
                            borderRadius: '6px',
                            background: member.isActive ? '#fef3c7' : '#d1fae5',
                            color: member.isActive ? '#92400e' : '#065f46',
                            border: 'none',
                            fontSize: '12px',
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          {statusId === member.id
                            ? '…'
                            : member.isActive
                            ? t('Home.Admin.Deactivate')
                            : t('Home.Admin.Activate')}
                        </button>
                      )}

                      {/* Delete — not for self */}
                      {member.id !== currentUserId && (
                        <button
                          onClick={() => handleDelete(member.id)}
                          disabled={deleteId === member.id}
                          style={{
                            padding: '5px 12px',
                            borderRadius: '6px',
                            background: '#fee2e2',
                            color: '#dc2626',
                            border: 'none',
                            fontSize: '12px',
                            fontWeight: 600,
                            cursor: 'pointer',
                          }}
                        >
                          {deleteId === member.id ? '…' : t('Home.Admin.Delete')}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* ── Add Member Modal ── */}
      {showModal && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setShowModal(false); }}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '16px',
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '460px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
              direction: isRTL ? 'rtl' : 'ltr',
            }}
          >
            {/* Modal header */}
            <div
              style={{
                padding: '20px 24px',
                borderBottom: '1px solid #f1f5f9',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <h5 style={{ margin: 0, fontWeight: 700, fontSize: '17px' }}>
                <i className="bi bi-person-plus me-2" style={{ color: '#f5a623' }} />
                {t('Home.Admin.AddMemberTitle')}
              </h5>
              <button
                onClick={() => setShowModal(false)}
                style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6c757d', lineHeight: 1 }}
              >
                ×
              </button>
            </div>

            {/* Modal body */}
            <form onSubmit={handleAdd} style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={labelStyle}>{t('Home.Admin.FullNameLabel')}</label>
                <input
                  style={inputStyle}
                  placeholder={t('Home.Admin.FullNamePlaceholder')}
                  value={form.fullName}
                  onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))}
                  required
                  autoFocus
                />
              </div>

              <div>
                <label style={labelStyle}>{t('Home.Admin.EmailLabel')}</label>
                <input
                  type="email"
                  style={inputStyle}
                  placeholder={t('Home.Admin.EmailPlaceholder')}
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  required
                />
              </div>

              <div>
                <label style={labelStyle}>{t('Home.Admin.PasswordLabel')}</label>
                <input
                  type="password"
                  style={inputStyle}
                  placeholder={t('Home.Admin.PasswordPlaceholder')}
                  value={form.password}
                  onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                  required
                  minLength={6}
                />
              </div>

              <div>
                <label style={labelStyle}>{t('Home.Admin.RoleLabel')}</label>
                <select
                  style={{ ...inputStyle, background: 'white' }}
                  value={form.role}
                  onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                >
                  <option value="member">{t('Home.Admin.MemberRole')}</option>
                  <option value="admin">{t('Home.Admin.AdminRole')}</option>
                </select>
              </div>

              {formError && (
                <div
                  style={{
                    background: '#fee2e2',
                    color: '#dc2626',
                    padding: '10px 14px',
                    borderRadius: '8px',
                    fontSize: '13px',
                  }}
                >
                  {formError}
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '4px' }}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: '1.5px solid #e5e7eb',
                    background: 'white',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    color: '#374151',
                  }}
                >
                  {t('Home.Admin.Cancel')}
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  style={{
                    padding: '10px 24px',
                    borderRadius: '8px',
                    background: 'linear-gradient(135deg, #f5a623, #e8920c)',
                    color: 'white',
                    border: 'none',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: saving ? 'not-allowed' : 'pointer',
                    opacity: saving ? 0.7 : 1,
                  }}
                >
                  {saving ? t('Home.Admin.SavingMember') : t('Home.Admin.SaveMember')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Change Password Modal ── */}
      {pwTarget && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setPwTarget(null); }}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '16px',
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '16px',
              width: '100%',
              maxWidth: '420px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
              direction: isRTL ? 'rtl' : 'ltr',
            }}
          >
            <div
              style={{
                padding: '20px 24px',
                borderBottom: '1px solid #f1f5f9',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <h5 style={{ margin: 0, fontWeight: 700, fontSize: '17px' }}>
                <i className="bi bi-key me-2" style={{ color: '#f5a623' }} />
                {t('Home.Admin.ChangePasswordTitle')}
              </h5>
              <button
                onClick={() => setPwTarget(null)}
                style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6c757d', lineHeight: 1 }}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleChangePassword} style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ fontSize: '13px', color: '#6c757d' }}>
                {pwTarget.fullName} · {pwTarget.email}
              </div>

              <div>
                <label style={labelStyle}>{t('Home.Admin.NewPasswordLabel')}</label>
                <input
                  type="password"
                  style={inputStyle}
                  placeholder={t('Home.Admin.NewPasswordPlaceholder')}
                  value={pwValue}
                  onChange={(e) => { setPwValue(e.target.value); setPwDone(false); }}
                  required
                  minLength={6}
                  autoFocus
                />
              </div>

              {pwError && (
                <div style={{ background: '#fee2e2', color: '#dc2626', padding: '10px 14px', borderRadius: '8px', fontSize: '13px' }}>
                  {pwError}
                </div>
              )}
              {pwDone && (
                <div style={{ background: '#d1fae5', color: '#065f46', padding: '10px 14px', borderRadius: '8px', fontSize: '13px' }}>
                  <i className="bi bi-check-circle me-1" />
                  {t('Home.Admin.PasswordUpdated')}
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '4px' }}>
                <button
                  type="button"
                  onClick={() => setPwTarget(null)}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: '1.5px solid #e5e7eb',
                    background: 'white',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    color: '#374151',
                  }}
                >
                  {t('Home.Admin.Cancel')}
                </button>
                <button
                  type="submit"
                  disabled={pwSaving}
                  style={{
                    padding: '10px 24px',
                    borderRadius: '8px',
                    background: 'linear-gradient(135deg, #f5a623, #e8920c)',
                    color: 'white',
                    border: 'none',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: pwSaving ? 'not-allowed' : 'pointer',
                    opacity: pwSaving ? 0.7 : 1,
                  }}
                >
                  {pwSaving ? t('Home.Admin.SavingMember') : t('Home.Admin.SavePassword')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
